import { useCallback, useEffect, useState } from 'react';
import type { AccessInfo } from '../api/client.js';
import type { ControllerSession } from '../controller.js';
import { fmtTime } from './common.js';

/** Admin: hand out one-time join codes and see who can run this event in Zoom. */
export function AccessPanel({ session }: { session: ControllerSession }) {
  const eventId = session.engine?.record.eventId;
  const [info, setInfo] = useState<AccessInfo | null>(null);
  const [fresh, setFresh] = useState<{ code: string; expiresAt: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const reload = useCallback(() => {
    if (!eventId) return;
    void session.client.access(eventId).then(setInfo).catch((e: unknown) => setErr(String(e)));
  }, [session, eventId]);
  useEffect(reload, [reload]);

  if (!eventId) return null;
  const run = async (fn: () => Promise<unknown>) => {
    setErr(null);
    try {
      await fn();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
    reload();
  };

  return (
    <div>
      <h2>Who can run this evening in Zoom</h2>
      <p className="muted">
        The person running the evening opens this app in Zoom and enters a join code once. A code works one time and only for this event. Anyone without a code sees nothing.
      </p>
      <div className="row">
        <button
          className="primary"
          onClick={() =>
            void run(async () => {
              const c = await session.client.createCode(eventId);
              setFresh({ code: c.code, expiresAt: c.expiresAt });
            })
          }
        >
          Create join code
        </button>
      </div>
      {fresh && (
        <div className="banner banner-info" role="status">
          <div className="timer" aria-label="join code">{fresh.code}</div>
          <div className="muted">Shown only now. Valid until {new Date(fresh.expiresAt).toLocaleString()}. Send it to the operator privately.</div>
        </div>
      )}
      {err && <div className="banner banner-error" role="alert">{err}</div>}

      <h2>Operators</h2>
      {info && info.operators.length === 0 && <p className="muted">Nobody has joined yet.</p>}
      {info && info.operators.length > 0 && (
        <table>
          <thead><tr><th>Zoom account</th><th>Joined</th><th /></tr></thead>
          <tbody>
            {info.operators.map((o) => (
              <tr key={o.zoomUserId}>
                <td>{o.zoomUserId}</td>
                <td>{new Date(o.boundAt).toLocaleString()}</td>
                <td><button className="secondary" onClick={() => void run(() => session.client.revokeOperator(eventId, o.zoomUserId))}>Remove</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h2>Join codes</h2>
      {info && info.codes.length === 0 && <p className="muted">No codes issued.</p>}
      {info && info.codes.length > 0 && (
        <table>
          <thead><tr><th>Issued</th><th>Expires</th><th>State</th><th /></tr></thead>
          <tbody>
            {info.codes.map((c) => {
              const expired = Date.parse(c.expiresAt) < Date.now();
              return (
                <tr key={c.id}>
                  <td>{fmtTime(c.createdAt)} {new Date(c.createdAt).toLocaleDateString()}</td>
                  <td>{new Date(c.expiresAt).toLocaleString()}</td>
                  <td>{c.usedAt ? 'used' : expired ? 'expired' : 'unused'}</td>
                  <td>{!c.usedAt && !expired && <button className="secondary" onClick={() => void run(() => session.client.revokeCode(eventId, c.id))}>Withdraw</button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
